import { ScaleLinear, scaleLinear } from 'd3-scale';
import { FC } from 'react';
import { flat, mapToObj, values } from 'remeda';
import { z } from 'zod';
import { groupMap, keyBy } from '../../collection';
import { geomOfWholePct } from '../../agg';
import { Rnge } from '../../range';
import { notNull, zx } from '../../type';
import { ChartCfg, ChartMeta } from './chartMeta.types';
import { ChartComponentProps, niceWholePctBounds, useDebouncedSize } from './charts';
import { G } from '../svg/G';
import { Svg } from '../svg/Svg';
import { binForecast, binForecastScale, ForecastBinBar, ForecastBinBarProps } from '../ForecastBar';
import { ForecastDelta } from './Bars';
import { XMetricLabelG } from './Labels';
import { LikelihoodBarOld } from './Conditionals';
import { MantineThemeColors, useSafeMantineTheme } from '@mantine/core';
const zn = zx.nullish;
const parser = {
  forecast: z.object({
    causeCode: zn.string.describe('Cause Code'),
    likelihood: zn.num.describe('Likelihood'),
    ifTrue: zn.num.describe('if true'),
    ifFalse: zn.num.describe('if false'),
    comments: zn.string.describe('comments')
  }),
  cause: z.object({
    causeCode: zn.string.describe('Cause Code'),
    title: zn.string.describe('Cause title'),
    ifTrue: zn.string.describe('If true label'),
    ifFalse: zn.string.describe('If false label')
  })
};
type CausesCfgSansSheets = {
  question: {
    title?: string;
    median?: number;
    color?: string;
  };
  deltaLabel?: string;
  ifTrue: {
    color?: string;
  };
  ifFalse: {
    color?: string;
  };
  likelihood: {
    color?: string;
  };
  upperBound?: number;
};
export type CausesCfg = ChartCfg<{
  forecast: z.infer<(typeof parser)['forecast']>[];
  cause: z.infer<(typeof parser)['cause']>[];
}> & CausesCfgSansSheets;
export type CausesProps = ChartComponentProps<{
  causes: CausesCfg['sheets']['cause'];
  forecasts: CausesCfg['sheets']['forecast'];
} & CausesCfgSansSheets> & {
  onHover?: (c?: Cause) => void;
  causeFilter?: string[] | null;
};
const type = 'CauseBars';
export const causeMeta: () => ChartMeta<CausesProps['chart'], CausesCfg> = () => ({
  type,
  title: 'Cause bars',
  View: Causes,
  edit: {
    initial: () => ({
      source: '',
      question: {
        title: '',
        median: 0
      },
      sheets: {
        cause: [],
        forecast: []
      },
      deltaLabel: '',
      ifTrue: {},
      ifFalse: {},
      likelihood: {}
    }) as CausesCfgSansSheets,
    parser: parser,
    cfgToProps: ({
      sheets,
      ...cfg
    }) => ({
      forecasts: sheets?.forecast ?? [],
      causes: sheets?.cause ?? [],
      ...cfg
    }),
    cfgSchema: {
      'question.title': {
        label: 'Question label',
        group: 'question'
      },
      'question.median': {
        label: 'Question median',
        type: 'number',
        precision: 1,
        group: 'question'
      },
      upperBound: {
        label: 'Override upper bound',
        type: 'number',
        group: 'question'
      },
      'ifTrue.color': {
        label: '"if true" cause color',
        type: 'color',
        group: 'color'
      },
      'ifFalse.color': {
        label: '"if false" cause color',
        type: 'color',
        group: 'color'
      },
      'likelihood.color': {
        label: 'likelihood color',
        type: 'color',
        group: 'color'
      },
      'question.color': {
        label: 'question color',
        type: 'color',
        group: 'color'
      },
      deltaLabel: {
        label: 'Cause change label',
        type: 'text'
      }
    }
  }
});
export const Causes: FC<CausesProps> = ({
  chart,
  className,
  onHover,
  causeFilter
}: CausesProps) => {
  const containerSize = useDebouncedSize();
  const {
    colors
  } = useSafeMantineTheme();
  const containerWidth = containerSize.width;
  const {
    title,
    question,
    likelihood
  } = chart;
  const barsBelow = containerWidth < 1400;
  const chartContainerWidth = barsBelow ? containerWidth : containerWidth * 0.5;
  const {
    byCause: byCause,
    scale,
    binScale,
    likelihoodScale
  } = layoutData({
    chart,
    width: chartContainerWidth,
    causeFilter,
    colors
  });
  return <div ref={containerSize.ref} className={`flex w-full flex-col gap-8 ${className ?? ''}`}>
      {title && <h2>{title}</h2>}
      {containerSize.width > 0 && byCause?.map((group, i) => {
      const isTop = i == 0;
      const barProps = (m: Mode): ForecastBinBarProps => ({
        value: group[m].value,
        bins: group.outcomeBins[m],
        scaleBinColor: binScale.copy().range([colors.gray[0], group[m]?.color ?? colors.blue[6]]),
        scale,
        showScale: isTop,
        showValue: false
      });
      const showQuestion = question.title && question.median;
      return <div key={i} className={`flex items-stretch`} style={{
        flexDirection: barsBelow ? 'column' : 'row',
        alignItems: barsBelow ? undefined : 'center'
      }} onMouseOver={() => onHover?.(group.cause)} onMouseOut={() => onHover?.(undefined)}>
              <h3 style={barsBelow ? {} : {
          flexBasis: containerWidth * 0.3
        }}>{group.cause.title}</h3>
              <div className='flex grow flex-row items-end'>
                <div className='grow'>
                  <Svg sizeMode='parent' style={{
              flexDirection: 'row',
              alignItems: 'baseline'
            }}>
                    <g>
                      <LikelihoodBarOld isTop={isTop} likelihood={group.likelihood.value} likelihoodScale={likelihoodScale} color={likelihood?.color} />
                    </g>
                    <g>
                      <G style={{
                  flexDirection: 'column',
                  paddingLeft: 40
                }}>
                        {isTop && showQuestion && <g>
                            <XMetricLabelG value={question.median!} label={question.title!} place='above' scale={scale} color={question.color} showTick />
                          </g>}
                        {showQuestion && <g>
                            <ForecastDelta from={{
                      label: question.title,
                      value: question.median ?? 0,
                      color: question.color
                    }} to={group.ifTrue} scale={scale} deltaLabel={isTop ? chart.deltaLabel : undefined} size='md' />
                          </g>}

                        <g>
                          <ForecastBinBar {...barProps('ifTrue')} />
                        </g>

                        {/* <g>{isTop && <AxisLabels scale={scale} />}</g> */}
                      </G>
                    </g>
                  </Svg>
                </div>
              </div>
            </div>;
    })}
    </div>;
};
type Cause = CausesProps['chart']['causes'][number];
type Forecast = CausesProps['chart']['forecasts'][number];
const layoutData = ({
  chart,
  width,
  causeFilter,
  colors
}: {
  chart: CausesProps['chart'];
  width: number;
  causeFilter?: string[] | null;
  colors: MantineThemeColors;
}) => {
  const {
    causes: causesRaw,
    upperBound
  } = chart;
  const causes = causesRaw.filter(c => c.causeCode != null);
  const causeByCode = keyBy(causes, c => c.causeCode!);
  const forecasts = chart.forecasts.filter(f => f.causeCode && (causeFilter == null || causeFilter.includes(f.causeCode)));
  const bounds = Rnge.clampRange(niceWholePctBounds(flat(forecasts.map(f => [f.ifTrue, f.ifFalse])).filter(notNull)), {
    min: 0,
    max: upperBound ?? 100
  });
  const barWidth = width * 0.5;
  const likelihoodWidth = width - barWidth - 85;
  const scale = scaleLinear(bounds, [0, barWidth]);
  const likelihoodScale = scaleLinear([0, 100], [0, likelihoodWidth]);
  const getBar = (forecasts: Forecast[], mode: Mode | 'likelihood', cause?: Cause) => {
    const all = forecasts.map(f => f[mode]).filter(notNull);
    return {
      value: geomOfWholePct(all) ?? 0,
      all,
      title: mode == 'likelihood' ? 'Likelihood' : cause?.[mode] ?? mode,
      color: chart[mode]?.color
    };
  };
  const byCause = groupMap(forecasts, o => o.causeCode!, (causeCode, forecasts, i) => {
    const cause = causeByCode[causeCode] ?? {
      causeCode,
      ifTrue: 'true',
      ifFalse: 'false',
      title: causeCode
    };
    return {
      forecasts,
      outcomeBins: modeBinForecasts({
        scale,
        forecasts
      }),
      likelihood: getBar(forecasts, 'likelihood'),
      ifTrue: getBar(forecasts, 'ifTrue', cause),
      ifFalse: getBar(forecasts, 'ifFalse', cause),
      cause,
      isTop: i == 0
    };
  });
  const binScale = binForecastScale({
    bins: flat(values(byCause)).flatMap(o => o.outcomeBins).flatMap(b => [...b.ifFalse, ...b.ifTrue]),
    colors
  });
  return {
    bounds,
    scale,
    byCause,
    binScale,
    likelihoodScale
  };
};
const modes = ['ifTrue', 'ifFalse'] as const;
type Mode = (typeof modes)[number];
const modeBinForecasts = ({
  scale,
  forecasts
}: {
  scale: ScaleLinear<number, number>;
  forecasts: Forecast[];
}) => mapToObj(modes, m => [m, binForecast({
  domain: scale,
  forecasts: forecasts.filter(f => notNull(f[m])).map(f => ({
    value: f[m]!,
    comments: f.comments?.split('|')
  }))
}).bins]);